import { useEffect, useMemo, useState } from 'react';
import type {
  BrandInvoiceMode,
  BrandInvoiceSettingsRecord,
  ReceiptInvoiceStatus,
  ReceiptOrderRecord,
  ReceiptOrderStatus,
} from '@nadoutong/contracts';
import {
  BRAND_IDS,
  BRAND_INVOICE_MODES,
  RECEIPT_CHANNELS,
  RECEIPT_CURRENCIES,
  RECEIPT_INVOICE_STATUSES,
  isBrandInvoiceMode,
  isReceiptInvoiceStatus,
  isReceiptOrderStatus,
} from '@nadoutong/contracts';
import { ExternalLink } from 'lucide-react';
import {
  useBrandInvoiceSettings,
  useReceiptDecision,
  useReceiptList,
  useUpdateBrandInvoiceSettings,
  type ReceiptListScope,
} from '../api/receipts.ts';
import { OpsApiError } from '../api/client.ts';
import {
  RECEIPT_FINANCE_DECISION_ROLES,
  ROLE_OPS_ADMIN,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { useCustomerLabel } from '../api/directory.ts';
import { ReceiptDetailDrawer } from '../components/ReceiptDetailDrawer.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  BRAND_INVOICE_MODE_LABEL_KEYS,
  RECEIPT_CHANNEL_LABEL_KEYS,
  RECEIPT_INVOICE_STATUS_LABEL_KEYS,
  RECEIPT_STATUS_LABEL_KEYS,
  formatReceiptMinor,
  formatTaxPointBps,
  parseTaxPointInputToBps,
  receiptInvoiceStatusChipTone,
  receiptStatusChipTone,
} from '../lib/receipts.ts';
import type { ReceiptDecisionAction } from '../api/receipts.ts';
import {
  Button,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Pagination,
  Panel,
  Select,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Receipt console (epic #1031 E2-R5, #1063) — one role-adaptive page:
 *
 *   - finance-manager / admin: the verification QUEUE (submitted
 *     receipts, 30s poll) front and center with credit / reject / void
 *     confirm dialogs (note mandatory on reject/void), then the full
 *     browse list behind it.
 *   - customer manager / sales lead: only the browse list — ops-api
 *     row-scopes it server-side (own submissions / brand book) and the
 *     matrix denies them decision endpoints outright. No verify
 *     controls render for sales sessions (#1063 AC 3).
 *
 * Decide race (#1063 AC 4): a 409 `RECEIPT_ORDER_NOT_SUBMITTED` is NOT
 * an error toast — the row flips to an inline "decided elsewhere" chip
 * and the shared `['receipts']` invalidation refreshes queue, list, and
 * any open detail to the true state. A 501 from a not-yet-deployed
 * credit path renders as a readable inline dialog error, never a stuck
 * spinner.
 *
 * Server-computed money only: this page never sends an amount — the
 * only receipt-money entry point is the sales submit dialog, and the
 * credit action carries nothing but an optional note.
 */

const PAGE_SIZE = 50;

const ACTION_TITLE_KEYS: Record<ReceiptDecisionAction, I18nKey> = {
  credit: 'receipts.decide.credit_title',
  reject: 'receipts.decide.reject_title',
  void: 'receipts.decide.void_title',
};

const ACTION_LABEL_KEYS: Record<ReceiptDecisionAction, I18nKey> = {
  credit: 'receipts.action.credit',
  reject: 'receipts.action.reject',
  void: 'receipts.action.void',
};

const ACTION_TOAST_KEYS: Record<ReceiptDecisionAction, I18nKey> = {
  credit: 'receipts.toast.credited',
  reject: 'receipts.toast.rejected',
  void: 'receipts.toast.voided',
};

/**
 * Shared customer cell: CRM company name first (the human-readable
 * identity finance reconciles against), org id + brand as the stable
 * machine identity below. `customer_company` is the LIVE CRM value —
 * "(not filled)" renders muted until sales completes the contact card.
 */
function CustomerCell({ receipt }: { readonly receipt: ReceiptOrderRecord }): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {receipt.customer_company !== null ? (
        <span className="truncate text-dense text-paper">{receipt.customer_company}</span>
      ) : (
        <span className="text-dense text-paper-low">{t('receipts.customer.company_missing')}</span>
      )}
      <span className="font-mono text-micro text-paper-low">
        {receipt.org_id} · {receipt.brand}
      </span>
    </div>
  );
}

export function ReceiptsPage(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const roles = user?.roles ?? [];
  const canDecide = hasAnyOpsRole(roles, RECEIPT_FINANCE_DECISION_ROLES);
  const isAdmin = hasAnyOpsRole(roles, [ROLE_OPS_ADMIN]);
  const [detailId, setDetailId] = useState<string | null>(null);

  return (
    <section className="flex flex-col gap-4" data-testid="receipts-page">
      <PageHeader
        title={t('receipts.title')}
        sub={canDecide ? t('receipts.subtitle.finance') : t('receipts.subtitle.sales')}
      />
      {canDecide ? <VerificationQueue onOpenDetail={setDetailId} /> : null}
      <BrowseList showBrandFilter={canDecide} onOpenDetail={setDetailId} />
      {isAdmin ? <BrandInvoiceSettingsPanel /> : null}
      <ReceiptDetailDrawer
        receiptId={detailId}
        onOpenChange={(open) => {
          if (!open) {
            setDetailId(null);
          }
        }}
      />
    </section>
  );
}

// ─── finance verification queue ──────────────────────────────────────────

interface DecideTarget {
  readonly receipt: ReceiptOrderRecord;
  readonly action: ReceiptDecisionAction;
}

function VerificationQueue({
  onOpenDetail,
}: {
  readonly onOpenDetail: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [offset, setOffset] = useState(0);
  const scope: ReceiptListScope = {
    limit: PAGE_SIZE,
    offset,
    status: 'submitted',
    brand: '',
    orgId: '',
    company: '',
    invoiceStatus: '',
  };
  const query = useReceiptList(scope, { enabled: true, poll: true });
  const decide = useReceiptDecision();

  const [target, setTarget] = useState<DecideTarget | null>(null);
  const [note, setNote] = useState('');
  const [decisionError, setDecisionError] = useState<string | null>(null);
  // Receipts whose decide race we lost: kept locally so the "decided
  // elsewhere" state survives the refetch removing them from the queue.
  const [decidedElsewhere, setDecidedElsewhere] = useState<
    ReadonlyMap<string, ReceiptOrderRecord>
  >(new Map());

  const data = query.data;
  const rows = useMemo<readonly ReceiptOrderRecord[]>(() => {
    const live = data?.receipts ?? [];
    const liveIds = new Set(live.map((row) => row.id));
    const stale = [...decidedElsewhere.values()].filter((row) => !liveIds.has(row.id));
    return [...stale, ...live];
  }, [data, decidedElsewhere]);

  function openDecide(receipt: ReceiptOrderRecord, action: ReceiptDecisionAction): void {
    setNote('');
    setDecisionError(null);
    setTarget({ receipt, action });
  }

  function submitDecision(): void {
    if (target === null || decide.isPending) {
      return;
    }
    const { receipt, action } = target;
    const trimmed = note.trim();
    if ((action === 'reject' || action === 'void') && trimmed.length === 0) {
      return;
    }
    setDecisionError(null);
    decide.mutate(
      { id: receipt.id, action, note: trimmed.length > 0 ? trimmed : null },
      {
        onSuccess: () => {
          setTarget(null);
          toast.success(t(ACTION_TOAST_KEYS[action]));
        },
        onError: (err) => {
          if (err instanceof OpsApiError && err.code === 'RECEIPT_ORDER_NOT_SUBMITTED') {
            // Lost the decide race: flip the row to its inline state —
            // deliberately no error toast; the onSettled invalidation
            // refreshes everything to the true decided state.
            setDecidedElsewhere((prev) => new Map(prev).set(receipt.id, receipt));
            setTarget(null);
            return;
          }
          if (err instanceof OpsApiError && err.status === 501) {
            setDecisionError(t('receipts.decide.error.not_implemented'));
            return;
          }
          setDecisionError(coerceErrorMessage(err));
        },
      },
    );
  }

  if (query.isPending) {
    return <TableSkeleton rows={4} testId="receipts-queue-loading" />;
  }
  if (query.isError || data === undefined) {
    return (
      <EmptyState
        title={t('receipts.load_error')}
        hint={coerceErrorMessage(query.error)}
        action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
        testId="receipts-queue-error"
      />
    );
  }

  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const noteMissing =
    target !== null &&
    (target.action === 'reject' || target.action === 'void') &&
    note.trim().length === 0;

  return (
    <Panel
      caption={t('receipts.queue.title')}
      count={data.total}
      testId="receipts-queue-panel"
    >
      <Table testId="receipts-queue-table" className="rounded-none border-0">
        <THead>
          <Tr interactive={false}>
            <Th>{t('receipts.col.customer')}</Th>
            <Th num>{t('receipts.col.amount')}</Th>
            <Th>{t('receipts.col.channel')}</Th>
            <Th num>{t('receipts.col.fee')}</Th>
            <Th>{t('receipts.col.received_at')}</Th>
            <Th>{t('receipts.col.entered_by')}</Th>
            <Th className="text-right">{t('receipts.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.length === 0 ? (
            <TableEmpty colSpan={7} testId="receipts-queue-empty">
              {t('receipts.queue.empty')}
            </TableEmpty>
          ) : (
            rows.map((receipt) => {
              const lostRace = decidedElsewhere.has(receipt.id);
              return (
                <Tr key={receipt.id} data-testid={`receipt-queue-row-${receipt.id}`}>
                  <Td>
                    <CustomerCell receipt={receipt} />
                  </Td>
                  <Td num emphasis>
                    {formatReceiptMinor(receipt.amount_minor, receipt.currency)}{' '}
                    {receipt.currency}
                  </Td>
                  <Td>{t(RECEIPT_CHANNEL_LABEL_KEYS[receipt.channel])}</Td>
                  <Td num>{formatReceiptMinor(receipt.actual_fee_minor, receipt.currency)}</Td>
                  <Td mono>{formatLocalTimestamp(receipt.received_at)}</Td>
                  <Td mono>
                    <OpsUserRef id={receipt.entered_by_ops_user_id} />
                  </Td>
                  <Td className="text-right">
                    {lostRace ? (
                      <Chip tone="neutral" testId={`receipt-decided-elsewhere-${receipt.id}`}>
                        {t('receipts.queue.decided_elsewhere')}
                      </Chip>
                    ) : (
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => openDecide(receipt, 'credit')}
                          data-testid={`receipt-credit-${receipt.id}`}
                        >
                          {t('receipts.action.credit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => openDecide(receipt, 'reject')}
                          data-testid={`receipt-reject-${receipt.id}`}
                        >
                          {t('receipts.action.reject')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDecide(receipt, 'void')}
                          data-testid={`receipt-void-${receipt.id}`}
                        >
                          {t('receipts.action.void')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onOpenDetail(receipt.id)}
                          data-testid={`receipt-queue-detail-${receipt.id}`}
                        >
                          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                          {t('receipts.action.detail')}
                        </Button>
                      </div>
                    )}
                  </Td>
                </Tr>
              );
            })
          )}
        </TBody>
      </Table>
      {pageCount > 1 ? (
        <div className="border-t border-line-soft px-4 py-3">
          <Pagination
            page={Math.floor(offset / PAGE_SIZE) + 1}
            pageCount={pageCount}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
            prevLabel={t('common.previous')}
            nextLabel={t('common.next')}
            ariaLabel={t('receipts.pagination.aria_label')}
          />
        </div>
      ) : null}
      <Dialog
        open={target !== null}
        onOpenChange={(open) => {
          if (!open && !decide.isPending) {
            setTarget(null);
          }
        }}
        title={target !== null ? t(ACTION_TITLE_KEYS[target.action]) : ''}
        description={
          target !== null ? (
            <ReceiptDecideDescription receipt={target.receipt} />
          ) : undefined
        }
        closeLabel={t('common.close')}
        testId="receipt-decide-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setTarget(null)} disabled={decide.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={target?.action === 'credit' ? 'primary' : 'danger'}
              onClick={submitDecision}
              loading={decide.isPending}
              disabled={noteMissing}
              data-testid="receipt-decide-confirm"
            >
              {target !== null ? t(ACTION_LABEL_KEYS[target.action]) : ''}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {target?.action === 'credit' ? (
            <p className="m-0 text-dense text-paper-mid">{t('receipts.decide.credit_hint')}</p>
          ) : null}
          <FormField
            label={t('receipts.decide.note_label')}
            fieldId="receipt-decide-note"
            required={target?.action === 'reject' || target?.action === 'void'}
            hint={
              target?.action === 'reject' || target?.action === 'void'
                ? t('receipts.decide.note_required_hint')
                : undefined
            }
          >
            <Textarea
              id="receipt-decide-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              data-testid="receipt-decide-note"
            />
          </FormField>
          {decisionError !== null ? (
            <p
              role="alert"
              data-testid="receipt-decide-error"
              className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
            >
              {decisionError}
            </p>
          ) : null}
        </div>
      </Dialog>
    </Panel>
  );
}

function ReceiptDecideDescription({ receipt }: { readonly receipt: ReceiptOrderRecord }): JSX.Element {
  const labels = useCustomerLabel();
  return (
    <>
      {formatReceiptMinor(receipt.amount_minor, receipt.currency)} {receipt.currency} ·{' '}
      {labels.byOrg(receipt.org_id)}
    </>
  );
}

// ─── browse list (all roles, server-scoped) ──────────────────────────────

const FILTER_ALL = '__all__';

interface ListFilters {
  readonly status: ReceiptOrderStatus | '';
  readonly brand: string;
  readonly currency: string;
  readonly channel: string;
  /** Applied CRM company-name search (server-side, submit-to-apply). */
  readonly company: string;
  readonly invoiceStatus: ReceiptInvoiceStatus | '';
  readonly offset: number;
}

function BrowseList({
  showBrandFilter,
  onOpenDetail,
}: {
  readonly showBrandFilter: boolean;
  readonly onOpenDetail: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const [filters, setFilters] = useState<ListFilters>({
    status: '',
    brand: '',
    currency: '',
    channel: '',
    company: '',
    invoiceStatus: '',
    offset: 0,
  });
  const [draftCompany, setDraftCompany] = useState('');
  // Invoice worklist composition (#1064 coupling): "invoice open" is
  // only actionable finance work on CREDITED receipts — the server
  // refuses issue/void otherwise (`RECEIPT_ORDER_NOT_CREDITED`) and
  // closes open invoices when a receipt is rejected/voided. Selecting
  // the open-invoice filter therefore pins the status filter to
  // `credited` so uncredited rows can never surface as invoice work.
  const invoiceWorklistPinned = filters.invoiceStatus === 'open';
  const effectiveStatus: ReceiptOrderStatus | '' = invoiceWorklistPinned
    ? 'credited'
    : filters.status;
  const scope: ReceiptListScope = {
    limit: PAGE_SIZE,
    offset: filters.offset,
    status: effectiveStatus,
    brand: filters.brand,
    orgId: '',
    company: filters.company,
    invoiceStatus: filters.invoiceStatus,
  };
  const query = useReceiptList(scope, { enabled: true });

  const data = query.data;
  // Currency/channel are not list parameters on the BFF — they narrow
  // the current page client-side.
  const rows = useMemo<readonly ReceiptOrderRecord[]>(() => {
    const fetched = data?.receipts ?? [];
    return fetched.filter(
      (row) =>
        (filters.currency === '' || row.currency === filters.currency) &&
        (filters.channel === '' || row.channel === filters.channel),
    );
  }, [data, filters.currency, filters.channel]);

  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  function setFilter(patch: Partial<ListFilters>): void {
    setFilters((prev) => ({ ...prev, ...patch, offset: 0 }));
  }

  return (
    <Panel
      caption={t('receipts.list.title')}
      count={data?.total}
      testId="receipts-list-panel"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setFilter({ company: draftCompany.trim() });
            }}
            data-testid="receipts-company-search-form"
          >
            <Input
              type="search"
              value={draftCompany}
              onChange={(event) => setDraftCompany(event.currentTarget.value)}
              placeholder={t('receipts.filter.company_placeholder')}
              aria-label={t('receipts.filter.company_label')}
              className="w-44"
              data-testid="receipts-company-search-input"
            />
            <Button type="submit" size="sm" variant="secondary" data-testid="receipts-company-search-submit">
              {t('receipts.filter.company_submit')}
            </Button>
            {filters.company.length > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraftCompany('');
                  setFilter({ company: '' });
                }}
                data-testid="receipts-company-search-clear"
              >
                {t('receipts.filter.company_clear')}
              </Button>
            ) : null}
          </form>
          <Select
            value={effectiveStatus === '' ? FILTER_ALL : effectiveStatus}
            onValueChange={(value) => {
              setFilter({ status: isReceiptOrderStatus(value) ? value : '' });
            }}
            disabled={invoiceWorklistPinned}
            options={[
              { value: FILTER_ALL, label: t('receipts.filter.status_all') },
              ...(['submitted', 'credited', 'rejected', 'voided'] as const).map((status) => ({
                value: status,
                label: t(RECEIPT_STATUS_LABEL_KEYS[status]),
              })),
            ]}
            ariaLabel={t('receipts.filter.status')}
            testId="receipts-filter-status"
          />
          {showBrandFilter ? (
            <Select
              value={filters.brand === '' ? FILTER_ALL : filters.brand}
              onValueChange={(value) =>
                setFilter({ brand: value === FILTER_ALL ? '' : value })
              }
              options={[
                { value: FILTER_ALL, label: t('receipts.filter.brand_all') },
                ...BRAND_IDS.map((brand) => ({ value: brand, label: brand })),
              ]}
              ariaLabel={t('receipts.filter.brand')}
              testId="receipts-filter-brand"
            />
          ) : null}
          <Select
            value={filters.currency === '' ? FILTER_ALL : filters.currency}
            onValueChange={(value) =>
              setFilter({ currency: value === FILTER_ALL ? '' : value })
            }
            options={[
              { value: FILTER_ALL, label: t('receipts.filter.currency_all') },
              ...RECEIPT_CURRENCIES.map((code) => ({ value: code, label: code })),
            ]}
            ariaLabel={t('receipts.filter.currency')}
            testId="receipts-filter-currency"
          />
          <Select
            value={filters.channel === '' ? FILTER_ALL : filters.channel}
            onValueChange={(value) =>
              setFilter({ channel: value === FILTER_ALL ? '' : value })
            }
            options={[
              { value: FILTER_ALL, label: t('receipts.filter.channel_all') },
              ...RECEIPT_CHANNELS.map((code) => ({
                value: code,
                label: t(RECEIPT_CHANNEL_LABEL_KEYS[code]),
              })),
            ]}
            ariaLabel={t('receipts.filter.channel')}
            testId="receipts-filter-channel"
          />
          <Select
            value={filters.invoiceStatus === '' ? FILTER_ALL : filters.invoiceStatus}
            onValueChange={(value) => {
              setFilter({ invoiceStatus: isReceiptInvoiceStatus(value) ? value : '' });
            }}
            options={[
              { value: FILTER_ALL, label: t('receipts.filter.invoice_all') },
              ...RECEIPT_INVOICE_STATUSES.map((status) => ({
                value: status,
                label: t(RECEIPT_INVOICE_STATUS_LABEL_KEYS[status]),
              })),
            ]}
            ariaLabel={t('receipts.filter.invoice')}
            testId="receipts-filter-invoice"
          />
        </div>
      }
    >
      {query.isPending ? <TableSkeleton rows={5} testId="receipts-list-loading" /> : null}
      {query.isError ? (
        <EmptyState
          title={t('receipts.load_error')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="receipts-list-error"
        />
      ) : null}
      {query.isSuccess ? (
        <>
          <Table testId="receipts-list-table" className="rounded-none border-0">
            <THead>
              <Tr interactive={false}>
                <Th>{t('receipts.col.status')}</Th>
                <Th>{t('receipts.col.customer')}</Th>
                <Th num>{t('receipts.col.amount')}</Th>
                <Th>{t('receipts.col.channel')}</Th>
                <Th>{t('receipts.col.invoice')}</Th>
                <Th>{t('receipts.col.received_at')}</Th>
                <Th>{t('receipts.col.entered_by')}</Th>
                <Th>{t('receipts.col.submitted_at')}</Th>
                <Th className="text-right">{t('receipts.col.actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.length === 0 ? (
                <TableEmpty colSpan={9} testId="receipts-list-empty">
                  {t('receipts.list.empty')}
                </TableEmpty>
              ) : (
                rows.map((receipt) => (
                  <Tr key={receipt.id} data-testid={`receipt-list-row-${receipt.id}`}>
                    <Td>
                      <Chip tone={receiptStatusChipTone(receipt.status)}>
                        {t(RECEIPT_STATUS_LABEL_KEYS[receipt.status])}
                      </Chip>
                    </Td>
                    <Td>
                      <CustomerCell receipt={receipt} />
                    </Td>
                    <Td num emphasis>
                      {formatReceiptMinor(receipt.amount_minor, receipt.currency)}{' '}
                      {receipt.currency}
                    </Td>
                    <Td>{t(RECEIPT_CHANNEL_LABEL_KEYS[receipt.channel])}</Td>
                    <Td>
                      {receipt.need_invoice ? (
                        <Chip tone={receiptInvoiceStatusChipTone(receipt.invoice_status)}>
                          {t(RECEIPT_INVOICE_STATUS_LABEL_KEYS[receipt.invoice_status])}
                          {receipt.tax_point_bps !== null
                            ? ` · ${formatTaxPointBps(receipt.tax_point_bps)}`
                            : ''}
                        </Chip>
                      ) : (
                        <span className="text-paper-low">—</span>
                      )}
                    </Td>
                    <Td mono>{formatLocalTimestamp(receipt.received_at)}</Td>
                    <Td mono>
                      <OpsUserRef id={receipt.entered_by_ops_user_id} />
                    </Td>
                    <Td mono>{formatLocalTimestamp(receipt.submitted_at)}</Td>
                    <Td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onOpenDetail(receipt.id)}
                        data-testid={`receipt-list-detail-${receipt.id}`}
                      >
                        <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                        {t('receipts.action.detail')}
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
          {pageCount > 1 ? (
            <div className="border-t border-line-soft px-4 py-3">
              <Pagination
                page={Math.floor(filters.offset / PAGE_SIZE) + 1}
                pageCount={pageCount}
                onPageChange={(page) =>
                  setFilters((prev) => ({ ...prev, offset: (page - 1) * PAGE_SIZE }))
                }
                prevLabel={t('common.previous')}
                nextLabel={t('common.next')}
                ariaLabel={t('receipts.pagination.aria_label')}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}

// ─── per-brand invoice settings (admin only, #1064) ─────────────────────

/**
 * Admin editor for `brand_invoice_settings` — the DB-config rows
 * that decide which invoice fields the
 * sales submit dialog collects and what tax point the platform accepts.
 * One editable row per registered brand: mode, default 票点 (%), cap
 * (%). Commercial mode has no tax point, so both inputs disable and
 * submit as null/0.
 */
function BrandInvoiceSettingsPanel(): JSX.Element {
  const t = useT();
  const settings = useBrandInvoiceSettings();

  return (
    <Panel caption={t('receipts.invoice_settings.title')} testId="brand-invoice-settings-panel">
      {settings.isPending ? <TableSkeleton rows={2} testId="brand-invoice-settings-loading" /> : null}
      {settings.isError ? (
        <EmptyState
          title={t('receipts.load_error')}
          hint={coerceErrorMessage(settings.error)}
          action={<Button onClick={() => void settings.refetch()}>{t('common.retry')}</Button>}
          testId="brand-invoice-settings-error"
        />
      ) : null}
      {settings.isSuccess ? (
        <div className="flex flex-col">
          <p className="m-0 border-b border-line-soft px-4 py-3 text-dense text-paper-mid">
            {t('receipts.invoice_settings.hint')}
          </p>
          {settings.data.settings.map((row) => (
            <BrandInvoiceSettingsRow key={row.brand} row={row} />
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

function BrandInvoiceSettingsRow({
  row,
}: {
  readonly row: BrandInvoiceSettingsRecord;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const update = useUpdateBrandInvoiceSettings();
  const [mode, setMode] = useState<BrandInvoiceMode>(row.mode);
  const [defaultTaxPoint, setDefaultTaxPoint] = useState(
    row.default_tax_point_bps !== null
      ? formatTaxPointBps(row.default_tax_point_bps).slice(0, -1)
      : '',
  );
  const [maxTaxPoint, setMaxTaxPoint] = useState(
    row.max_tax_point_bps > 0 ? formatTaxPointBps(row.max_tax_point_bps).slice(0, -1) : '',
  );
  const [rowError, setRowError] = useState<string | null>(null);

  // Server state changed under us (another admin saved): resync.
  useEffect(() => {
    setMode(row.mode);
    setDefaultTaxPoint(
      row.default_tax_point_bps !== null
        ? formatTaxPointBps(row.default_tax_point_bps).slice(0, -1)
        : '',
    );
    setMaxTaxPoint(
      row.max_tax_point_bps > 0 ? formatTaxPointBps(row.max_tax_point_bps).slice(0, -1) : '',
    );
  }, [row.mode, row.default_tax_point_bps, row.max_tax_point_bps]);

  function save(): void {
    if (update.isPending) {
      return;
    }
    let defaultBps: number | null = null;
    let maxBps = 0;
    if (mode === 'cn-tax') {
      const parsedMax = parseTaxPointInputToBps(maxTaxPoint);
      if (!parsedMax.ok || parsedMax.bps === 0) {
        setRowError(t('receipts.invoice_settings.error.max'));
        return;
      }
      maxBps = parsedMax.bps;
      if (defaultTaxPoint.trim().length > 0) {
        const parsedDefault = parseTaxPointInputToBps(defaultTaxPoint);
        if (!parsedDefault.ok || parsedDefault.bps > maxBps) {
          setRowError(t('receipts.invoice_settings.error.default'));
          return;
        }
        defaultBps = parsedDefault.bps;
      }
    }
    setRowError(null);
    update.mutate(
      { brand: row.brand, mode, defaultTaxPointBps: defaultBps, maxTaxPointBps: maxBps },
      {
        onSuccess: () => {
          toast.success(t('receipts.invoice_settings.toast.saved', { brand: row.brand }));
        },
        onError: (err) => {
          setRowError(coerceErrorMessage(err));
        },
      },
    );
  }

  return (
    <div
      className="flex flex-wrap items-end gap-3 border-b border-line-soft px-4 py-3 last:border-b-0"
      data-testid={`brand-invoice-settings-row-${row.brand}`}
    >
      <div className="flex min-w-24 flex-col gap-0.5">
        <span className="text-label text-paper-low">{t('receipts.invoice_settings.brand')}</span>
        <span className="font-mono text-dense text-paper">{row.brand}</span>
      </div>
      <FormField
        label={t('receipts.invoice_settings.mode')}
        fieldId={`brand-invoice-mode-${row.brand}`}
      >
        <Select
          value={mode}
          onValueChange={(value) => {
            if (isBrandInvoiceMode(value)) {
              setMode(value);
            }
          }}
          options={BRAND_INVOICE_MODES.map((candidate) => ({
            value: candidate,
            label: t(BRAND_INVOICE_MODE_LABEL_KEYS[candidate]),
          }))}
          ariaLabel={t('receipts.invoice_settings.mode')}
          testId={`brand-invoice-mode-${row.brand}`}
        />
      </FormField>
      <FormField
        label={t('receipts.invoice_settings.default_tax_point')}
        fieldId={`brand-invoice-default-${row.brand}`}
      >
        <Input
          id={`brand-invoice-default-${row.brand}`}
          value={mode === 'commercial' ? '' : defaultTaxPoint}
          onChange={(event) => setDefaultTaxPoint(event.currentTarget.value)}
          placeholder="6"
          inputMode="decimal"
          disabled={mode === 'commercial'}
          className="w-24"
          data-testid={`brand-invoice-default-${row.brand}`}
        />
      </FormField>
      <FormField
        label={t('receipts.invoice_settings.max_tax_point')}
        fieldId={`brand-invoice-max-${row.brand}`}
      >
        <Input
          id={`brand-invoice-max-${row.brand}`}
          value={mode === 'commercial' ? '' : maxTaxPoint}
          onChange={(event) => setMaxTaxPoint(event.currentTarget.value)}
          placeholder="13"
          inputMode="decimal"
          disabled={mode === 'commercial'}
          className="w-24"
          data-testid={`brand-invoice-max-${row.brand}`}
        />
      </FormField>
      <Button
        size="sm"
        variant="secondary"
        onClick={save}
        loading={update.isPending}
        data-testid={`brand-invoice-save-${row.brand}`}
      >
        {t('common.save')}
      </Button>
      {rowError !== null ? (
        <p
          role="alert"
          data-testid={`brand-invoice-error-${row.brand}`}
          className="m-0 basis-full text-dense text-bad-bright"
        >
          {rowError}
        </p>
      ) : null}
    </div>
  );
}
